'use client';
import { Map, Placemark, YMaps } from '@pbe/react-yandex-maps';
import { useState, useRef, useCallback } from 'react';
import styles from './map.module.scss';
import { YMapsApi } from '@pbe/react-yandex-maps/typings/util/typing';
import { IGeoObject } from 'yandex-maps';


const API_KEY = process.env.NEXT_PUBLIC_YANDEX_MAPS_API_KEY;

type GeoMapProps = {
    onAddressSelect: (address: string, coords: number[]) => void;
    initialCenter?: number[];
};

const GeoMap = ({
    onAddressSelect,
    initialCenter = [41.2995, 69.2401],
}: GeoMapProps) => {
    const [selectedCoords, setSelectedCoords] = useState<number[] | null>(null);
    const [currentAddress, setCurrentAddress] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const ymapsRef = useRef<YMapsApi | null>(null);

    const handleApiLoad = useCallback((ymapsInstance: YMapsApi) => {
        ymapsRef.current = ymapsInstance;
    }, []);

    const geocodeCoords = useCallback(
        async (coords: number[]) => {
            if (!ymapsRef.current?.geocode) {
                setError('Карта или функция геокодирования еще не загружена.');
                return;
            }

            setIsLoading(true);
            setError(null);
            setSelectedCoords(coords);
            setCurrentAddress('Определение адреса...');

            try {
                const res = await ymapsRef.current.geocode(coords);
                const firstGeoObject: IGeoObject | undefined = res.geoObjects.get(0);

                if (firstGeoObject) {
                    const addressText = firstGeoObject.properties.get('text', undefined as any);
                    const addressName = firstGeoObject.properties.get('name', undefined as any);

                    const address: string = String(addressText || addressName || 'Адрес не определен');

                    setCurrentAddress(address);
                    onAddressSelect(address, coords);

                } else {
                    setError('Не удалось определить адрес для этой точки.');
                    const notFoundMsg = 'Адрес не найден';
                    setCurrentAddress(notFoundMsg);
                    onAddressSelect(notFoundMsg, coords);
                }
            } catch (e) {
                const errorMessage = e instanceof Error ? e.message : String(e);
                setError(`Ошибка при определении адреса: ${errorMessage}`);
                const errorMsg = 'Ошибка определения адреса';
                setCurrentAddress(errorMsg);
                onAddressSelect(errorMsg, coords);
            } finally {
                setIsLoading(false);
            }
        },
        [onAddressSelect],
    );

    const handleMapClick = useCallback(
        (e: any) => {
            const coords = e.get('coords');
            if (coords && Array.isArray(coords) && coords.length === 2) {
                if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
                    geocodeCoords(coords as number[]);
                } else {
                    setError("Некорректные координаты точки.")
                }
            }
        },
        [geocodeCoords],
    );


    return (
        <div className={styles.mapWrapper}>
            <YMaps
                query={{
                    lang: 'ru_RU',
                    load: 'package.full',
                    apikey: API_KEY,
                }}
            >
                <Map
                    instanceRef={(ref) => {
                        if (ref) {
                            ref.behaviors.disable('scrollZoom');
                        }
                    }}
                    onLoad={handleApiLoad}
                    state={{
                        center: selectedCoords || initialCenter,
                        zoom: 15,
                        controls: ['fullscreenControl', 'zoomControl', 'geolocationControl'],
                    }}
                    style={{ width: '100%', height: '440px' }}
                    modules={[
                        'control.FullscreenControl',
                        'control.ZoomControl',
                        'control.GeolocationControl',
                        'geocode',
                        'geoObject.addon.balloon',
                        'geoObject.addon.hint',
                        'behavior.Drag', // Убедимся, что перетаскивание включено
                        // 'behavior.ScrollZoom' // Это мы отключаем через instanceRef
                    ]}
                    onClick={handleMapClick}
                    onError={(err: Error) => setError(`Ошибка загрузки карты: ${err.message}`)}
                >
                    {selectedCoords && (
                        <Placemark
                            geometry={selectedCoords}
                            options={{
                                preset: 'islands#redDotIcon',
                                // draggable: true, // Можно разрешить перетаскивание метки
                                // onDragEnd: (e: any) => {
                                //     const newCoords = e.get('target').geometry.getCoordinates();
                                //     if (newCoords) {
                                //         geocodeCoords(newCoords);
                                //     }
                                // }
                            }}
                            properties={{
                                hintContent: isLoading ? 'Загрузка...' : (error || currentAddress),
                                balloonContent: isLoading ? 'Определение адреса...' : (error || currentAddress),
                            }}
                        />
                    )}
                </Map>
            </YMaps>
        </div>
    );
};

export default GeoMap;